import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { fmtMoney, formatNZDateTime, fuzzyIncludes, getItemsSummary, saleSearchText } from '../lib/reportUtils'

const MIN_QUERY = 2

export default function GlobalSearch({ onNavigate }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState({ customers: [], sales: [], products: [], payments: [] })
  const timerRef = useRef(null)

  useEffect(() => {
    if (!open || query.trim().length < MIN_QUERY) {
      setRows({ customers: [], sales: [], products: [], payments: [] })
      return
    }

    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => runSearch(query.trim()), 250)
    return () => clearTimeout(timerRef.current)
  }, [query, open])

  const runSearch = async term => {
    setLoading(true)
    const safe = term.replaceAll('%', '').replaceAll(',', ' ')
    const amount = Number(safe.replace(String.fromCharCode(36), ''))

    const [customersRes, salesRes, productsRes, paymentsRes] = await Promise.all([
      supabase.from('customers').select('*').or('name.ilike.%' + safe + '%,phone.ilike.%' + safe + '%,email.ilike.%' + safe + '%').limit(8),
      supabase.from('sales').select('*').order('created_at', { ascending: false }).limit(100),
      supabase.from('products').select('*').order('category').limit(100),
      supabase.from('customer_payments').select('*').order('created_at', { ascending: false }).limit(60)
    ])

    const sales = (salesRes.data || []).filter(s => {
      const amountMatch = !Number.isNaN(amount) && String(s.total_amount || '').includes(String(amount))
      return amountMatch || fuzzyIncludes(saleSearchText(s), safe)
    }).slice(0, 8)

    const products = (productsRes.data || []).filter(p =>
      fuzzyIncludes([p.name, p.category, p.price].filter(Boolean).join(' '), safe)
    ).slice(0, 8)

    const payments = (paymentsRes.data || []).filter(p => {
      const amountMatch = !Number.isNaN(amount) && String(p.amount || '').includes(String(amount))
      return amountMatch || fuzzyIncludes([p.payment_method, p.amount, p.note, formatNZDateTime(p.created_at)].filter(Boolean).join(' '), safe)
    }).slice(0, 6)

    setRows({
      customers: customersRes.data || [],
      sales,
      products,
      payments
    })
    setLoading(false)
  }

  const totalResults = useMemo(() => Object.values(rows).reduce((sum, list) => sum + list.length, 0), [rows])

  const closeAndGo = tab => {
    setOpen(false)
    setQuery('')
    onNavigate && onNavigate(tab)
  }

  return (
    <div style={{ flex: 1, maxWidth: 520, margin: '0 12px', position: 'relative' }}>
      <input
        value={query}
        onFocus={() => setOpen(true)}
        onChange={e => { setQuery(e.target.value); setOpen(true) }}
        placeholder="Search customer, amount, item, payment..."
        style={{ width: '100%', padding: '8px 11px', border: 'none', borderRadius: 8, fontSize: 13, outline: 'none', background: 'rgba(255,255,255,0.95)', color: '#1a3a2a' }}
      />
      {open && query && (
        <div style={{ position: 'absolute', top: 42, left: 0, right: 0, background: '#fff', color: '#1a3a2a', borderRadius: 12, boxShadow: '0 12px 30px rgba(0,0,0,0.18)', border: '1px solid #e5e7eb', maxHeight: 430, overflowY: 'auto', zIndex: 300 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>Global Search</div>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18 }}>×</button>
          </div>

          {query.trim().length < MIN_QUERY ? (
            <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>Type at least 2 characters.</div>
          ) : loading ? (
            <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>Searching...</div>
          ) : totalResults === 0 ? (
            <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>No matching customers, sales, products or payments.</div>
          ) : (
            <>
              <ResultGroup title="Customers" rows={rows.customers} onShowAll={() => closeAndGo('customers')} render={c => (
                <ResultRow key={c.id} title={c.name || 'Unnamed customer'} meta={[c.phone, c.email, Number(c.outstanding_balance || 0) > 0 ? 'Owes ' + fmtMoney(c.outstanding_balance) : 'Clear'].filter(Boolean).join(' · ')} onClick={() => closeAndGo('customers')} />
              )} />
              <ResultGroup title="Sales / Transactions" rows={rows.sales} onShowAll={() => closeAndGo('reports')} render={s => (
                <ResultRow key={s.id} title={fmtMoney(s.total_amount) + ' · ' + (s.customer_name || 'Walk-in')} meta={(s.payment_method || 'Unknown') + ' · ' + formatNZDateTime(s.created_at) + ' · ' + getItemsSummary(s.items)} onClick={() => closeAndGo('reports')} />
              )} />
              <ResultGroup title="Products / Items" rows={rows.products} onShowAll={() => closeAndGo('settings')} render={p => (
                <ResultRow key={p.id} title={p.name} meta={(p.category || 'Product') + ' · ' + fmtMoney(p.price) + (p.active === false ? ' · Archived' : '')} onClick={() => closeAndGo('settings')} />
              )} />
              <ResultGroup title="Customer Payments" rows={rows.payments} onShowAll={() => closeAndGo('customers')} render={p => (
                <ResultRow key={p.id} title={fmtMoney(p.amount) + ' payment'} meta={(p.payment_method || 'Unknown') + ' · ' + formatNZDateTime(p.created_at) + (p.note ? ' · ' + p.note : '')} onClick={() => closeAndGo('customers')} />
              )} />
            </>
          )}
        </div>
      )}
    </div>
  )
}

function ResultGroup({ title, rows, render, onShowAll }) {
  if (!rows || rows.length === 0) return null
  return (
    <div style={{ borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px 4px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</div>
        <button onClick={onShowAll} style={{ border: 'none', background: 'none', color: '#1a6b3c', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Open</button>
      </div>
      {rows.map(render)}
    </div>
  )
}

function ResultRow({ title, meta, onClick }) {
  return (
    <button onClick={onClick} style={{ width: '100%', border: 'none', background: '#fff', padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#1a3a2a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta}</div>
    </button>
  )
}
